import React, { useEffect, useState, useCallback } from "react";
import Layout from '../../components/layout/Layout';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement, BarElement } from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
// import { getData, postFileData, imageUrl } from '../../services/apiService';
import { getData, getPresignedUrl, uploadToS3, postData } from '../../services/apiService';
import { useDropzone } from 'react-dropzone';
import { Box, Button, Typography, Modal, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Progress from '../../hooks/useProgressBar';


// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
);

const InstituteProfile = () => {
  const [loading, setLoading] = useState(true);
  const [instituteData, setInstituteData] = useState([]);
  const [studentCountData, setStudentCountData] = useState([]);
  const [totalStudent, setTotalStudent] = useState([]);
  const [totalCourse, setTotalCourse] = useState([]);
  const [pendingStudent, setPendingStudent] = useState([]);
  const [activeStudents, setActiveStudents] = useState([]);
  // const [totalInstructor, setTotalInstructor] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  useEffect(() => {
    getInstiuteProfile();
  }, []);

  // Get Institute Profile Data
  const getInstiuteProfile = async () => {
    setLoading(true);
    try {
      const response = await getData('get-instiute-Profile');
      if (response) {
        setInstituteData(response.data);
        setStudentCountData(response.student);
        // setTotalInstructor(response.totalInstructor);
        setTotalStudent(response.totalStudent);
        setTotalCourse(response.totalCourse);
        setActiveStudents(response.activeStudents);
        setPendingStudent(response.pendingStudent);
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch data', error);
      setLoading(false);
    }
  }

  // Transform student count data for chart
  const transformStudentData = useCallback(() => {
    if (!studentCountData || studentCountData.length === 0) {
      return {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
      };
    }

    // Initialize array with 12 months, all set to 0
    const monthlyData = new Array(12).fill(0);

    // Map the student count data to the correct month index (month - 1 because array is 0-indexed)
    studentCountData.forEach(item => {
      if (item.month >= 1 && item.month <= 12) {
        monthlyData[item.month - 1] = item.count;
      }
    });

    return {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      data: monthlyData
    };
  }, [studentCountData]);

  // Sample data - replace with your actual data
  const chartData = {
    labels: transformStudentData().labels,
    datasets: [
      {
        label: 'Students',
        data: transformStudentData().data,
        borderColor: 'rgba(106, 106, 201, 1)',
        backgroundColor: 'rgba(106, 106, 201, 0.6)',
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 20,
        ticks: {
          stepSize: 2,
        },
      },
    },
  };

  // Enrollment status doughnut chart data
  const enrollmentData = {
    labels: ['Total Students', 'Active Students', 'Pending Students'],
    datasets: [
      {
        data: [totalStudent, activeStudents, pendingStudent], // 70% completed, 20% enrolled, 10% dropped
        backgroundColor: [
          '#7978E9',
          '#4B49AC',
          '#98BDFF',
        ],
        borderColor: [
          '#7978E9',
          '#4B49AC',
          '#98BDFF',
        ],
        borderWidth: 2,
        cutout: '70%',
      },
    ],
  };

  const enrollmentOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.label}: ${context.parsed}%`;
          }
        }
      }
    },
  };

  const coursesData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Courses',
        data: [4, 8, 6, 5, 14, 7, 8, 12, 6, 15, 16, 9],
        backgroundColor: 'rgba(106, 106, 201, 0.6)',
        borderColor: 'rgba(106, 106, 201, 1)',
        borderWidth: 1,
      },
    ],
  };

  const coursesOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 20,
        ticks: {
          stepSize: 2,
        },
      },
    },
  };



  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    maxFiles: 1
  });

  const handleImageUpload = async () => {
    if (!selectedFile) return;

    try {
      // Get presigned URL for S3 upload
      const { uploadUrl, fileKey, publicUrl } = await getPresignedUrl(selectedFile.type);

      // Upload to S3
      await uploadToS3(uploadUrl, selectedFile);

      // Update profile with the new image URL
      const response = await postData('/update-profile-image', {
        profileImage: publicUrl,
        fileKey
      });

      if (response) {
        setInstituteData(prev => ({ ...prev, profileImage: publicUrl }));
        setShowImageModal(false);
        setSelectedFile(null);
        setPreviewUrl(null);
      }
      getInstiuteProfile();
    } catch (error) {
      console.error('Failed to upload image', error);
    }
  };

  // const handleImageUpload = async () => {
  //   if (!selectedFile) return;

  //   try {
  //     const formData = new FormData();
  //     formData.append('profileImage', selectedFile);

  //     const response = await postFileData('/update-profile-image', formData);
  //     if (response) {
  //       setInstituteData(prev => ({ ...prev, profileImage: response?.profile?.profileImage }));
  //       setShowImageModal(false);
  //       setSelectedFile(null);
  //       setPreviewUrl(null);
  //     }
  //   } catch (error) {
  //     console.error('Failed to upload image', error);
  //   }
  // };

  return (
    <Layout>
      {loading ? <Progress /> :
        <div className="p-4 sm:p-6 lg:p-8 xl:p-10 bg-gray-100 min-h-screen w-full">
          <div className="max-w-none xl:max-w-8xl 2xl:max-w-9xl mx-auto">
            {/* Header */}
            <div className="mb-6 lg:mb-8">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-black">
                View Profile
              </h1>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-4 2xl:grid-cols-5 gap-6 lg:gap-8">
              {/* Left Column - Profile and Charts */}
              <div className="xl:col-span-1 2xl:col-span-2 space-y-6 lg:space-y-8">
                {/* Profile Card */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                  <div className="relative">
                    {/* Header Background */}
                    <div className="h-20 lg:h-24 bg-gradient-to-r from-[rgba(10,68,175,0.6)] to-[rgba(181,205,249,1)] rounded-t-lg"></div>
                    {/* Profile Image */}
                    <div className="absolute top-9 lg:top-11 left-4 lg:left-6">
                      <div className="relative">
                        <div className="w-20 h-20 lg:w-24 lg:h-24 rounded-full flex items-center justify-center border-4 border-white border-opacity-80 shadow-sm">
                          {/* <img
                          className="w-16 h-16 lg:w-20 lg:h-20 rounded-full object-cover"
                          src={`${imageUrl}${instituteData?.profileImage}`}
                          alt="Profile"
                        /> */}
                          <img
                            className="w-16 h-16 lg:w-20 lg:h-20 rounded-full object-cover"
                            src={instituteData?.profileImage || ''}
                            alt="Profile"
                          />
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 lg:w-7 lg:h-7 bg-black bg-opacity-40 rounded-full flex items-center justify-center cursor-pointer"
                          onClick={() => setShowImageModal(true)} >
                          <img className="w-3 h-3 lg:w-4 lg:h-4" src="https://c.animaapp.com/mc0bh65gR5TIhU/img/vector-5.svg" alt="Edit" />
                        </div>
                      </div>
                    </div>

                    {/* Edit Button */}
                    <div className="absolute top-2 lg:top-3 right-4 lg:right-6">
                      {/* <button className="bg-white rounded px-3 py-1 lg:px-4 lg:py-2 flex items-center gap-2 text-sm lg:text-base shadow-sm">
                      <img
                        className="w-4 h-4 lg:w-5 lg:h-5"
                        src="https://c.animaapp.com/mc0bh65gR5TIhU/img/vector.svg"
                        alt="Edit"
                      />
                      <span>Edit</span>
                    </button> */}
                    </div>

                    {/* Institution Badge */}
                    <div className="absolute top-20 lg:top-24 right-4 lg:right-6">
                      <div className="flex items-center gap-2 bg-green-50 px-3 py-1 lg:px-4 lg:py-2 rounded-full">
                        <div className="w-2 h-2 lg:w-3 lg:h-3 bg-green-500 rounded-full"></div>
                        <span className="text-green-600 text-sm lg:text-base font-medium">{instituteData?.college_id?.instituteType}</span>
                      </div>
                    </div>
                  </div>

                  {/* Profile Info */}
                  <div className="p-6 lg:p-6 pt-16 lg:pt-20">
                    <h2 className="text-lg lg:text-xl font-semibold text-gray-800 mb-1 lg:mb-2">
                      {instituteData?.college_id?.collegeName}
                    </h2>
                    <p className="text-sm lg:text-base text-gray-600">
                      {instituteData?.college_id?.address}, {instituteData?.college_id?.pinCode}
                    </p>
                  </div>
                </div>

                {/* Enrollment Status Card */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 lg:p-6">
                  <h3 className="text-lg lg:text-xl font-semibold text-black mb-4 lg:mb-6">Enrollment Status</h3>
                  <div className="flex flex-col xl:flex-row items-center gap-6 lg:gap-8">
                    <div className="w-32 h-32 lg:w-40 lg:h-40">
                      <Doughnut data={enrollmentData} options={enrollmentOptions} />
                    </div>
                    <div className="space-y-3 lg:space-y-4">
                      <div className="flex items-center gap-2 lg:gap-3">
                        <div className="w-3 h-3 lg:w-4 lg:h-4 rounded" style={{ backgroundColor: '#7978E9' }}></div>
                        <span className="text-sm lg:text-base font-medium">Total Students</span>
                      </div>
                      <div className="flex items-center gap-2 lg:gap-3">
                        <div className="w-3 h-3 lg:w-4 lg:h-4 rounded" style={{ backgroundColor: '#4B49AC' }}></div>
                        <span className="text-sm lg:text-base font-medium">Active Students</span>
                      </div>
                      <div className="flex items-center gap-2 lg:gap-3">
                        <div className="w-3 h-3 lg:w-4 lg:h-4 rounded" style={{ backgroundColor: '#98BDFF' }}></div>
                        <span className="text-sm lg:text-base font-medium">Pending Students</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Courses Chart Card */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 lg:p-6">
                  <h3 className="text-lg lg:text-xl font-semibold text-black mb-4 lg:mb-6">Courses</h3>
                  <div className="h-48 lg:h-56 xl:h-64">
                    <Bar data={coursesData} options={coursesOptions} />
                  </div>
                </div>
              </div>

              {/* Right Column - Overview and Students Chart */}
              <div className="xl:col-span-3 2xl:col-span-3 space-y-6 lg:space-y-8">
                {/* Overview Card */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 lg:p-8">
                  <h3 className="text-lg lg:text-xl font-semibold text-black mb-6 lg:mb-8">Overview</h3>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 lg:gap-6">
                    {/* Courses */}
                    <div className="bg-gray-50 rounded-lg p-4 lg:p-6 border border-purple-100">
                      <div className="flex items-center gap-3 lg:gap-4">
                        <img
                          className="w-5 h-5 lg:w-6 lg:h-6"
                          src="https://c.animaapp.com/mc0bh65gR5TIhU/img/vector-1.svg"
                          alt="Courses"
                        />
                        <div>
                          <h4 className="font-semibold text-black text-base lg:text-lg">Courses</h4>
                          <p className="text-xs lg:text-sm text-gray-600">{totalCourse} courses</p>
                        </div>
                      </div>
                    </div>

                    {/* Instructor */}
                    {/* <div className="bg-gray-50 rounded-lg p-4 lg:p-6 border border-purple-100">
                    <div className="flex items-center gap-3 lg:gap-4">
                      <img
                        className="w-5 h-5 lg:w-6 lg:h-6"
                        src="https://c.animaapp.com/mc0bh65gR5TIhU/img/vector-4.svg"
                        alt="Instructor"
                      />
                      <div>
                        <h4 className="font-semibold text-black text-base lg:text-lg">Instructor</h4>
                        <p className="text-xs lg:text-sm text-gray-600">{totalInstructor} Instructor</p>
                      </div>
                    </div>
                  </div> */}

                    {/* Enrollment */}
                    <div className="bg-gray-50 rounded-lg p-4 lg:p-6 border border-purple-100">
                      <div className="flex items-center gap-3 lg:gap-4">
                        <img
                          className="w-5 h-5 lg:w-6 lg:h-6"
                          src="https://c.animaapp.com/mc0bh65gR5TIhU/img/vector-2.svg"
                          alt="Enrollment"
                        />
                        <div>
                          <h4 className="font-semibold text-black text-base lg:text-lg">Student</h4>
                          <p className="text-xs lg:text-sm text-gray-600">{totalStudent} Student</p>
                        </div>
                      </div>
                    </div>

                    {/* Ranking */}
                    <div className="bg-gray-50 rounded-lg p-4 lg:p-6 border border-purple-100">
                      <div className="flex items-center gap-3 lg:gap-4">
                        <img
                          className="w-5 h-5 lg:w-6 lg:h-6"
                          src="https://c.animaapp.com/mc0bh65gR5TIhU/img/vector-3.svg"
                          alt="Ranking"
                        />
                        <div>
                          <h4 className="font-semibold text-black text-base lg:text-lg">Ranking</h4>
                          <p className="text-xs lg:text-sm text-gray-600">4.5</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Students Chart Card */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 lg:p-8">
                  <h3 className="text-lg lg:text-xl font-semibold text-black mb-6 lg:mb-8">Students</h3>
                  <div className="h-80 lg:h-96 xl:h-[28rem]">
                    <Line data={chartData} options={chartOptions} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
      {/* Image Upload Modal */}
      <Modal
        open={showImageModal}
        onClose={() => {
          setShowImageModal(false);
          setSelectedFile(null);
          setPreviewUrl(null);
        }}
        aria-labelledby="image-upload-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="h2">
              Update Profile Image
            </Typography>
            <IconButton
              onClick={() => {
                setShowImageModal(false);
                setSelectedFile(null);
                setPreviewUrl(null);
              }}
              sx={{ position: 'absolute', right: 8, top: 8, color: 'gray' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 1,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              mb: 2,
              bgcolor: isDragActive ? 'action.hover' : 'background.paper',
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography>
              {isDragActive
                ? "Drop the image here"
                : "Drag and drop an image here, or click to select"}
            </Typography>
          </Box>

          {previewUrl && (
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <img
                src={previewUrl}
                alt="Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '200px',
                  borderRadius: '4px',
                }}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setShowImageModal(false);
                setSelectedFile(null);
                setPreviewUrl(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleImageUpload}
              disabled={!selectedFile}
            >
              Upload
            </Button>
          </Box>
        </Box>
      </Modal>

    </Layout>
  );
};

export default InstituteProfile;